import { useEffect, useState, useRef } from 'react';
import { useFormik } from 'formik';
import { getStore } from 'app/hermes-redux';
import Language from 'language';
import HelpCenterComp from 'react-components/help-center-comp/help-center-comp';
import TextInputField from 'wa-storybook/components/inputs/simple-input-field/text-input-comp';
import Spinner from 'wa-storybook/components/general/spinner/spinner';
import Button from 'wa-storybook/components/general/button/button';
import styles from './invitation-code-form.module.scss';
import { getUrlParameter, goTo } from 'router/navigation';
import { lookupKeys, performTenancyLookup } from 'globals/regional-api';
import { setCompany } from 'stores/company-store';
import fetch from 'globals/wa-fetch';
import * as Storage from 'globals/storage';
import { updateData } from '../../signup/main/state/actions';
import getFormSchemas from './form-schemas';
import {
  errorCodes,
  parseErrorCode,
} from 'wa-storybook/global/constants/error-messages';
import { showToast } from 'react-components/toaster-comp/state/actions';
import { ToastType } from 'react-components/toaster-comp/state/types';
import { consentService } from '../../consent/consent-service';
import urlencode from 'globals/php-urlencode-polyfill';
import { Company as CompanyT } from 'wa-storybook/global/types/company.type';
import { Link } from 'router/custom-link';
import ComponentAnimation, {
  AnimationTypes,
} from 'wa-storybook/components/general/component-animation/component-animation';
import { getZendeskLink } from 'state/global/actions';

type Result = {
  body: {
    token: string;
    company: CompanyT;
  };
};

type InitialValues = { invitation_code?: string; otp: string };

export const getInitDataForCode = (code: string, otp = '') => {
  return fetch(
    `/user/signup-invitation-code/${urlencode(code)}${
      otp ? `/${urlencode(otp)}` : ''
    }`,
    {
      version: 1.3,
    },
  ).then((result: Result) => {
    Storage.set(Storage.getKeys().signupSession, result.body.token);

    return result;
  });
  // we deliberately don't handle error and just pass the result up
  // because an error code is used to indicate that we need to open the OTP field
};

const InvitationCodeForm = () => {
  const [isValidatingQueryStringCode, setIsValidatingQueryStringCode] =
    useState(false);
  const [isOtpFieldAtLeastPartiallyOpen, setIsOtpFieldAtLeastPartiallyOpen] =
    useState(false);
  const [isOtpFieldOpen, setIsOtpFieldOpen] = useState(false);
  const { invitationCodeOnly, invitationCodeAndOtp } = getFormSchemas();
  const [hasError, setHasError] = useState(false);
  const [validationSchema, setValidationSchema] = useState(invitationCodeOnly);

  const submitInvitationDetails = async (data, formikBag?) => {
    const { dispatch } = getStore();

    try {
      await performTenancyLookup(lookupKeys.inviteCode, data.invitation_code);

      const { body: initData } = await getInitDataForCode(
        data.invitation_code,
        data.otp,
      );
      // again, the error is deliberately not caught here. It is used to open the OTP field

      setCompany(initData.company);

      const { locale: companyLocale } = initData.company;

      if (
        !Language.getLoginLanguagePersistence() &&
        Language.getLocale() !== companyLocale
      ) {
        Language.set({ locale: initData.company.locale });
      }

      dispatch(
        updateData('initData', {
          ...initData,
          code: data.invitation_code,
          otp: data.otp,
        }),
      );

      await consentService.loadConsentsData();

      goTo('signup', { code: urlencode(data.invitation_code) });
    } catch (res) {
      const { code, fields = [] } = (res as any).error || {};

      const isOtpFieldError = fields?.includes('otp');

      if (code === parseErrorCode(errorCodes['signup_validation'][0])) {
        if (!isOtpFieldError) {
          setHasError(true);
          setIsValidatingQueryStringCode(false);
          return;
        }

        if (!data.otp) {
          setValidationSchema(invitationCodeAndOtp);

          if (formikBag) {
            formikBag.resetForm({
              values: {
                invitation_code: data.invitation_code,
                otp: '',
              },
            });
          }

          setIsOtpFieldAtLeastPartiallyOpen(true);
          setIsOtpFieldOpen(true);
        } else {
          setValidationSchema(invitationCodeAndOtp);
        }

        if (isOtpFieldAtLeastPartiallyOpen) {
          setHasError(true);

          if (formikBag) {
            formikBag.resetForm({
              values: {
                invitation_code: data.invitation_code,
                otp: data.otp,
              },
            });
          }
        }

        setIsValidatingQueryStringCode(false);
      } else {
        dispatch(
          showToast({
            type: ToastType.ERROR,
            message: polyglot.t('sign_up.error'),
          }),
        );
      }
    }
  };

  const invitationCode = getUrlParameter('code');

  const initialValues: InitialValues = {
    invitation_code: invitationCode,
    otp: '',
  };

  const formik = useFormik({
    initialValues,
    onSubmit: submitInvitationDetails,
    validationSchema,
  });

  useEffect(() => {
    if (invitationCode) {
      setIsValidatingQueryStringCode(true);

      submitInvitationDetails({
        invitation_code: invitationCode,
        otp: '',
      });
    }
  }, []);

  const {
    errors = {},
    handleSubmit,
    isSubmitting,
    dirty,
    isValid,
    setFieldValue,
    validateForm,
  } = formik;

  const otpRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (otpRef.current && isOtpFieldOpen) {
      otpRef.current.focus();
    }
  }, [otpRef, isOtpFieldOpen]);

  return (
    <form onSubmit={handleSubmit} className={styles.container}>
      <div className={styles.content}>
        {isValidatingQueryStringCode ? (
          <div className={styles.overlay}>
            <Spinner top={'calc(50% - 30px)'} />
          </div>
        ) : (
          <>
            <h1 className={styles.title}>
              {polyglot.t('sign_up.invitation.sign_up')}
            </h1>
            <div>
              <div className={styles['input-field']}>
                <TextInputField
                  id='invitation_code'
                  label={polyglot.t('sign_up.invitation.invitation_code')}
                  onChange={async value => {
                    if (isOtpFieldAtLeastPartiallyOpen) {
                      setValidationSchema(invitationCodeOnly);

                      formik.resetForm({
                        values: {
                          otp: '',
                        },
                      });

                      setIsOtpFieldAtLeastPartiallyOpen(false);
                      setIsOtpFieldOpen(false);
                      setHasError(false);

                      await validateForm();
                    }

                    setFieldValue('invitation_code', value);
                  }}
                  type='text'
                  parentErrorMessage={errors['invitation_code']}
                  value={invitationCode}
                />
              </div>
              {isOtpFieldOpen && (
                <ComponentAnimation
                  type={AnimationTypes.EXPAND_DOWN_AND_FADE_IN}
                  id='invitation-code'
                >
                  <div className={styles['input-field']}>
                    <TextInputField
                      ref={otpRef}
                      id='otp'
                      label={polyglot.t('sign_up.invitation.unique_code')}
                      onChange={value => {
                        setFieldValue('otp', value);

                        setHasError(false);
                      }}
                      type='text'
                      parentErrorMessage={errors['otp']}
                      isDisabled={!isOtpFieldAtLeastPartiallyOpen}
                    />
                  </div>
                </ComponentAnimation>
              )}
              {hasError && (
                <ComponentAnimation
                  type={AnimationTypes.EXPAND_DOWN_AND_FADE_IN}
                  id='signup-error-mesage'
                >
                  <div role='alert' className={styles.error}>
                    {isOtpFieldOpen
                      ? polyglot.t('sign_up.invitation.incorrect_data')
                      : polyglot.t('sign_up.invitation.valid_code')}
                  </div>
                </ComponentAnimation>
              )}
              {isOtpFieldOpen && (
                <ComponentAnimation
                  type={AnimationTypes.EXPAND_DOWN_AND_FADE_IN}
                  id='disclaimer-mesage'
                >
                  <div className={styles.disclaimer}>
                    {polyglot.t('sign_up.invitation.contact')}
                  </div>
                </ComponentAnimation>
              )}
              {!isOtpFieldOpen && (
                <>
                  <div className={styles.disclaimer}>
                    {polyglot.t('sign_up.invitation.once')}
                  </div>
                  <div className={styles.link}>
                    <Link to='/signup/invitation/resend-invitation'>
                      {polyglot.t('sign_up.invitation.button_cannot_find')}
                    </Link>
                  </div>
                </>
              )}
            </div>
            <div className={styles.button}>
              <Button
                dataHook='signup'
                text={polyglot.t('sign_up.invitation.next')}
                isDisabled={!dirty || !isValid}
                isLoading={isSubmitting}
              />
            </div>
          </>
        )}
      </div>
      <div className={styles.info}>
        <HelpCenterComp
          helpText={polyglot.t(
            'sign_up.invitation.help_and_accessibility_text_link',
            {
              urlGettingStarted: getZendeskLink({ key: 'getting_started' }),
              urlAccessibilityTips: getZendeskLink({
                key: 'accessibility_tips',
              }),
            },
          )}
        />
      </div>
    </form>
  );
};

InvitationCodeForm.displayName = 'SignupForm';

export default InvitationCodeForm;
